import CssBaseline from '@material-ui/core/CssBaseline';
import { createStyles, createTheme, makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import React, { createContext, useCallback, useContext, useState } from 'react';
import Cookies from 'universal-cookie';
import Companys from '../interfaces/Companys';
import { Supplier } from '../interfaces/Supplier';
import ConciergeApi from '../services/MvmApi';
import SupplierApi from '../services/SupplierApi';
import api from '../services/UserApi';
import { TOKEN_KEY, USER_KEY, VIEW_KEY } from '../utils/util';

var themeFCA = createTheme({
    palette: {
        primary: {
            // light: will be calculated from palette.primary.main,
            main: '#597DDA',
            // dark: will be calculated from palette.primary.main,
            // contrastText: will be calculated to contrast with palette.primary.main
        },
        secondary: {
            // light: will be calculated from palette.primary.main,
            main: '#ffffff',
            // dark: will be calculated from palette.primary.main,
            // contrastText: will be calculated to contrast with palette.primary.main
        },
    },
    typography: {
        fontSize: 13,
        fontFamily: ['Helvetica Neue', 'Roboto,Arial', 'Droid Sans', 'sans-serif'].join(','),
    },
    spacing: 8
});

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            background: '#F8F9FB',
        },
    }),
);

interface User {
    name: string;
    userName: string;
    email: string;
    token: string;
    permissions: [];
    suppliers: [];
    concierge: string;
    loadConcierge: boolean;
    isSupplier: boolean;
    isSupplierDetails: boolean;
    entities: [{
        id: string;
        code: string;
        name: string;
        type: string;
        dealerCode: string;
    }];
    userViews: [{
        id: string;
        code: string;
        name: string;
        type: string;
    }];    
    userMenu: [
        {
            id: string;
            applicationId: string;
            parentMenuId: string;
            name: string;
            url: string;
            target: string;
            icon: string;
            subMenu: [
                {
                    id: string;
                    applicationId: string;
                    parentMenuId: string;
                    name: string;
                    url: string;
                    target: string;
                    icon: string;                    
                }
            ];
        }
    ];
    company:
    {
        companyID: string;
        companyCode: string;
        warehouseID: string; 
        companyName: string; 
        companyBOMCode: string; 
        plantID: string;
    }
}

interface SignInCredentials {
    userName: string;
    password: string;
}

interface SignInTokenCredentials {
    token: string;
}

interface SetViewInterface {
    view: string;
}

interface AuthContextData {
    user: User;
    view: string;
    signIn(credentials: SignInCredentials): Promise<void>;
    signInToken(credentials: SignInTokenCredentials): Promise<void>;
    signOut(): void;
    setView(view: SetViewInterface): Promise<void>;
    hasPermission(permission: string): boolean;
    syncSuppliers(): Promise<void>;
    isCampoLargo(): boolean;
    isJeep(): boolean;
    isMopar(): boolean;
    isFiasa(): boolean;
    isPortoReal(): boolean;
}
interface AuthState {
    token: string;
    user: User;
    view: string;
}
const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
    const classes = useStyles();
    const cookies = new Cookies();
    const [data, setData] = useState<AuthState>(() => {
        const user = localStorage.getItem(USER_KEY);
        const token = localStorage.getItem(TOKEN_KEY);
        const view = localStorage.getItem(VIEW_KEY);
        if (user && token && view) {
            return { token: token, user: JSON.parse(user), view };
        }
        return {} as AuthState;
    });

    const syncSuppliers = useCallback(async () => {
        //Obter dados completos dos Fornecedores do usuario, alem de codigo e nome:
        if (data.user && !data.user.isSupplierDetails)
        {
            let data_ = data;

            await ConciergeApi.userConcierge(data.user?.userName, +data.user?.company?.plantID).then((result) => {
                data_.user.concierge = result ? result : '';
            }).catch(err => {
                console.log('get user concierge error', err);
            });

            let supplierIds = data.user.suppliers.map((x:any) => (x.code));

            await SupplierApi.searchSuppliers(supplierIds, data.user.company.plantID)
            .then(result => { 
                data_.user.suppliers = result.items.map((supplier: Supplier) => {
                        return { 
                            label: supplier.supplierID + " - " + supplier.name, 
                            code: supplier.supplierID,
                            name: supplier.name,
                            cnpj: supplier.cnpj,
                            companyId: supplier.companyID,
                            uf: supplier.region,
                            sapCode: supplier.supplierSAP,
                            cdlcCode: supplier.codeSupplierCDLC
                        };
                    });            
                    data_.user.isSupplierDetails = true;
                    setData(data_);
                    localStorage.setItem(USER_KEY, JSON.stringify(data_.user));
            })
            .catch(err => {
                console.error('Get Suppliers details error', err.message);
            });
        }
    }, []);

    const loadUserData = async (userResult: any, view: any) => {
        let userMenu: any = [];
        
        const decoded = JSON.parse(decodeURIComponent(escape(atob(userResult.data.acesstoken.split('.')[1]))));
        let userSuppliers = JSON.parse(decoded.entities).filter((entity: any) => entity.type === 'SUPPLIER' && entity.code.substring(0, 13) === 'SUPPLIER_IMS_').map((entity: any) => {
            return {
                code: entity.code.substring(13),
                name: entity.name,
                label: entity.code.substring(13) + ' - ' + entity.name };
        });   
        
        if (!userResult.data)
            return;
        
        let user = {
            name: decoded.nameid,
            userName: decoded.unique_name,
            email: decoded.email === "" ? decoded.nameid : decoded.email,
            token: userResult.data.acesstoken,
            tokenSecurity: JSON.parse(decoded.tokenSecurity),
            isSupplier: userSuppliers.some((x:any) => (true)),
            isSupplierDetails: false,
            suppliers: userSuppliers,
            permissions: decoded.role === undefined ? [] : decoded.role,
            entities: decoded.entities === undefined ? [] : JSON.parse(decoded.entities),
            userViews: decoded.userViews === undefined ? [] : JSON.parse(decoded.userViews),
            userMenu: userMenu === undefined ? [] : userMenu,
            company: Companys[(view || '') as keyof typeof Companys] as any,
            concierge: '',
            loadConcierge: true,
        };
        
        if ((view == undefined || view == '')) {
            view = user.userViews[0].code;
            user.company = Companys[view as keyof typeof Companys] as any;
        }

 
		await api.getUserMenu(JSON.parse(decoded.tokenSecurity), view).then((result) => {
            userMenu = result.data;
            user.userMenu = result.data;
        }).catch(err => {
            console.log(err);
        });        
        
        //Armazenar primeiramente o token no storage - pode ser necessario na chamada a Api Supplier, abaixo
        localStorage.setItem(TOKEN_KEY, user.token);
        
        //Obter dados completos dos Fornecedores do usuario, alem de codigo e nome:
        if (user.isSupplier)
        {
            let supplierIds = user.suppliers.map((x:any) => (x.code));

            await SupplierApi.searchSuppliers(supplierIds, user.company.plantID)
            .then(result => {
                    user.suppliers = result.items.map((supplier: Supplier) => {
                        return { 
                            label: supplier.supplierID + " - " + supplier.name, 
                            code: supplier.supplierID,
                            name: supplier.name,
                            cnpj: supplier.cnpj,
                            companyId: supplier.companyID,
                            uf: supplier.region,
                            sapCode: supplier.supplierSAP,
                            cdlcCode: supplier.codeSupplierCDLC
                        };
                    });       
                    user.isSupplierDetails = true;  
                })
            .catch(err => {               
                console.error(err.message);
            });
        }

        //Entao, armazenar os demais itens:
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        localStorage.setItem(VIEW_KEY, view);
        cookies.remove("FCA-security", {path: "/", domain: ".fcalatam.com.br"})

        setData({
            token: user.token,
            user,
            view
        });
    }

    const signIn = useCallback(async ({ userName, password, view }) => {
        if ((view == undefined || view == '') && (localStorage.getItem(VIEW_KEY) != undefined || localStorage.getItem(VIEW_KEY) != '')) {
            view = localStorage.getItem(VIEW_KEY);
        }

		await api.getUser({
            userName,
            password,
            view
        }).then(async (userResult) => {
			
            if (!userResult.data)
                return;

            await loadUserData(userResult, view);
        }).catch(err => {
            cookies.remove("FCA-security", {path: "/", domain: ".fcalatam.com.br"});
        });

    }, []);

    const signInToken = useCallback(async ({ token, view }) => {
        if ((view == undefined || view == '') && (localStorage.getItem(VIEW_KEY) != undefined || localStorage.getItem(VIEW_KEY) != '')) {
            view = localStorage.getItem(VIEW_KEY);
        }
        
        await api.getUserToken(
            token,
            view
        ).then(async (userResult) => {
            
            if (!userResult.data)
                return;

            await loadUserData(userResult, view);

        }).catch(err => {
            cookies.remove("FCA-security", {path: "/", domain: ".fcalatam.com.br"})
            console.error(err.message);
        });


    }, [cookies]);

    const signOut = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        cookies.remove("FCA-security", {path: "/", domain: ".fcalatam.com.br"})
        setData({} as AuthState);
        window.location.href = '/';
    }, []);

    const setView = useCallback(async ({ view }) => {
        localStorage.setItem(VIEW_KEY, view);

        const user = localStorage.getItem(USER_KEY);
        let tokenSecurity = "";
        if (user){
            tokenSecurity = JSON.parse(user).tokenSecurity;
        }

        const token = localStorage.getItem(TOKEN_KEY);
        const companyToken = localStorage.getItem(VIEW_KEY);
       
        await signInToken({token: tokenSecurity, view});
    }, []);

    const hasPermission = useCallback((permission: never) => {
        return data?.user?.permissions.indexOf(permission) > -1 ? true : false;
    }, [data?.user]);

    const isCampoLargo = () => {
        const objUser = localStorage.getItem(USER_KEY);
        let user:User|null = null;
        if (objUser)
            user = JSON.parse(objUser);

        return Number(user?.company?.companyID) === 170;
    }

    const isJeep = () => {
        const objUser = localStorage.getItem(USER_KEY);
        let user:User|null = null;
        if (objUser)
            user = JSON.parse(objUser);

        return Number(user?.company?.companyID) === 167;
    }

    const isMopar = () => {
        const objUser = localStorage.getItem(USER_KEY);
        let user:User|null = null;
        if (objUser)
            user = JSON.parse(objUser);

        return Number(user?.company?.companyID) === 241;
    }
    const isFiasa = () => {
        const objUser = localStorage.getItem(USER_KEY);
        let user:User|null = null;
        if (objUser)
            user = JSON.parse(objUser);

        return Number(user?.company?.companyID) === 154;
    }    
    
    const isPortoReal = () => {
        const objUser = localStorage.getItem(USER_KEY);
        let user:User|null = null;
        if (objUser)
            user = JSON.parse(objUser);

        return Number(user?.company?.companyID) === 243;
    }
    return (
        <AuthContext.Provider
            value={{ user: data.user, view: data.view, signIn, signInToken, signOut, setView, hasPermission, syncSuppliers, isCampoLargo, isJeep, isMopar, isFiasa, isPortoReal }}
        >
            <ThemeProvider theme={themeFCA}>
                <CssBaseline />
                <div className={classes.root}>
                    {children}
                </div>
            </ThemeProvider>
        </AuthContext.Provider>
    );
};

function useAuth(): AuthContextData {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
export { AuthProvider, useAuth };
